import { forwardRef, type ReactNode } from 'react';

// Root Empty container
interface EmptyProps {
  children: ReactNode;
  className?: string;
}

const Empty = forwardRef<HTMLDivElement, EmptyProps>(
  ({ children, className = '' }, ref) => {
    return (
      <div
        ref={ref}
        className={`flex flex-col items-center justify-center p-16 text-center ${className}`}
      >
        {children}
      </div>
    );
  }
);
Empty.displayName = 'Empty';

// Header section
interface EmptyHeaderProps {
  children: ReactNode;
  className?: string;
}

const EmptyHeader = forwardRef<HTMLDivElement, EmptyHeaderProps>(
  ({ children, className = '' }, ref) => {
    return (
      <div
        ref={ref}
        className={`flex flex-col items-center gap-3 max-w-xs ${className}`}
      >
        {children}
      </div>
    );
  }
);
EmptyHeader.displayName = 'EmptyHeader';

// Media/Icon section
interface EmptyMediaProps {
  children: ReactNode;
  className?: string;
  variant?: 'icon' | 'default';
}

const EmptyMedia = forwardRef<HTMLDivElement, EmptyMediaProps>(
  ({ children, className = '', variant = 'icon' }, ref) => {
    const baseStyles = 'flex items-center justify-center mb-2';
    const variantStyles = variant === 'icon'
      ? 'bg-default-100 rounded-xl p-2 text-default-900'
      : '';

    return (
      <div
        ref={ref}
        className={`${baseStyles} ${variantStyles} ${className}`}
      >
        {children}
      </div>
    );
  }
);
EmptyMedia.displayName = 'EmptyMedia';

// Title section
interface EmptyTitleProps {
  children: ReactNode;
  className?: string;
}

const EmptyTitle = forwardRef<HTMLHeadingElement, EmptyTitleProps>(
  ({ children, className = '' }, ref) => {
    return (
      <h3
        ref={ref}
        className={`text-lg font-semibold text-default-900 leading-6 ${className}`}
      >
        {children}
      </h3>
    );
  }
);
EmptyTitle.displayName = 'EmptyTitle';

// Description section
interface EmptyDescriptionProps {
  children: ReactNode;
  className?: string;
}

const EmptyDescription = forwardRef<HTMLParagraphElement, EmptyDescriptionProps>(
  ({ children, className = '' }, ref) => {
    return (
      <p
        ref={ref}
        className={`text-sm text-default-500 max-w-xs ${className}`}
      >
        {children}
      </p>
    );
  }
);
EmptyDescription.displayName = 'EmptyDescription';

// Content/Actions section
interface EmptyContentProps {
  children: ReactNode;
  className?: string;
}

const EmptyContent = forwardRef<HTMLDivElement, EmptyContentProps>(
  ({ children, className = '' }, ref) => {
    return (
      <div
        ref={ref}
        className={`flex flex-col items-center gap-2 mt-4 ${className}`}
      >
        {children}
      </div>
    );
  }
);
EmptyContent.displayName = 'EmptyContent';

export {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
};
